import React, { ReactElement } from "react";
import usePortal from "../../../contexts/PortalContext";
import {
    Integration, IntegrationActionType, useGetAllIntegrationsQuery,
} from "../../../__generated__/api";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

type PropClearable<TIsClearable, T> = TIsClearable extends true ? T | undefined : T;

export interface DeflectionSourcePickerProps<TIsMulti = false, TIsClearable = false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    isClearable?: TIsClearable;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (
        sourceId: PropClearable<TIsClearable, (TIsMulti extends true ? string[] : string)>,
        display: PropClearable<TIsClearable, (TIsMulti extends true ? string[] : string)>,
    ) => void;
}

const DEFAULT_OFF = {
    label: "Off",
    value: undefined as unknown as string,
};

const DeflectionSource = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, isClearable,
}: DeflectionSourcePickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetAllIntegrationsQuery();
    const { id: portalId } = usePortal();

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<Integration>[] : Option<Integration>;
    type ValueType = TIsMulti extends true ? Option<Integration>[] : Option<Integration>;

    const handleSelected = (selected: SelectedType) => {
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<Integration>)?.value) as Parameters<typeof onSelected>[0];

        const texts = (Array.isArray(selected)
            ? selected.map(row => row.label as string)
            : (selected as Option<Integration>)?.label) as Parameters<typeof onSelected>[0];
        onSelected(values, texts);
    };

    const integrations = (data?.integrations ?? [])
        .filter(row => row.actions?.some(type => type === IntegrationActionType.AnswerProvider));

    const values = [
        DEFAULT_OFF,
        {
            label: "Foqal Answers",
            value: portalId as string,
        },
    ].concat(integrations.map(integration => ({
        label: integration.label as string,
        value: `urn:fq:workflow-service-answer-source:${integration.id}`,
    })));

    const valueMap = values.toIdMap("value");
    const selected = (Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string]) ?? DEFAULT_OFF;

    return (
        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            isClearable={isClearable}
            options={values ?? []}
            value={selected as ValueType}
        />
    );
});

DeflectionSource.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    isClearable: false,
};

export default DeflectionSource as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false,
> (
    props: DeflectionSourcePickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof DeflectionSource>;
